import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Database, Store, Tag, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { ErrorState } from "@/components/empty-state";

export const Route = createFileRoute("/admin/")({
  component: Overview,
});

function Overview() {
  const q = useQuery({ queryKey: ["admin-overview"], queryFn: () => api.adminOverview(), retry: false });

  if (q.isError) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold">Dashboard</h1>
        <ErrorState message={(q.error as Error).message + " — make sure the NexKey backend is running and CORS is configured."} />
      </div>
    );
  }
  const d = q.data?.data;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Real-time snapshot from the NexKey API.</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Database} label="Games" value={d?.games ?? "—"} sub={`${d?.pendingGames ?? 0} pending`} href="/admin/games" />
        <Stat icon={Store} label="Shops" value={d?.shops ?? "—"} href="/admin/shops" />
        <Stat icon={Tag} label="Offers" value={d?.offers ?? "—"} href="/admin/offers" />
        <Stat icon={AlertCircle} label="Last import" value={d?.lastJob?.status ?? "never"} sub={d?.lastJob?.source ?? ""} href="/admin/import" />
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Getting started</h2>
        <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
          <li>1. Open <Link to="/admin/import" className="text-primary">Steam import</Link> and pull the Steam app list.</li>
          <li>2. Run a bulk details import to fetch full game info from Steam.</li>
          <li>3. Add <Link to="/admin/shops" className="text-primary">shops</Link> manually and create real <Link to="/admin/offers" className="text-primary">offers</Link>.</li>
          <li>4. Visit the public homepage to see live data — empty states stay until real content exists.</li>
        </ol>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, href }: { icon: any; label: string; value: any; sub?: string; href: string }) {
  return (
    <Link to={href as any} className="block rounded-xl border border-border bg-card p-4 hover:border-primary">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-2 font-display text-3xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </Link>
  );
}
